import type { ComponentProps } from 'react';
import React, { useState } from 'react';
import styled from '@emotion/styled';
import type { Nullable, StringMap } from 'corgi-types';
import type { JustifyContentProps } from 'styled-system';
import { justifyContent, space } from 'styled-system';

import DrawerForm from 'common/components/DrawerForm';
import Spinner from 'common/components/Spinner';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import ConfigurationTrap from './ConfigurationTrap';
import { useFetchConfigurationTrap } from './hooks/fetch';
import { trackSaveButtonClick } from './tracking';

const SPINNER_SIZE = 24;

interface ConfigurationTrapDrawerBodyProps {
  alternativeVariantKey: string;
  alternativeVariantValue: string;
  selectedValues: StringMap<string>;
  sku: string;
  title: string;
}
const UnstyledStyledSpinner = styled(Spinner)<SpaceProps & JustifyContentProps>`
  ${space};
  ${justifyContent};
`;

const StyledSpinner = (props: ComponentProps<typeof UnstyledStyledSpinner>) => (
  <UnstyledStyledSpinner p="sp_8" justifyContent="center" {...props} />
);

const ConfigurationTrapDrawerBody = ({
  title,
  sku,
  alternativeVariantKey,
  alternativeVariantValue,
  selectedValues,
}: ConfigurationTrapDrawerBodyProps) => {
  const { t } = useLocalization();
  const [redirectURL, setRedirectURL] = useState<Nullable<string>>(null);

  const {
    isLoading,
    data: alternativeVariations,
    error,
    listLength,
  } = useFetchConfigurationTrap({
    sku,
    alternativeVariantKey,
    alternativeVariantValue,
  });

  const ctaLabel = t('h24_save');

  const onSubmit = () => {
    if (redirectURL) {
      trackSaveButtonClick(ctaLabel);
      window.location.href = redirectURL;
    }
  };

  return (
    <DrawerForm
      ctaLabel={ctaLabel}
      isCtaLoading={false}
      isCtaActive={Boolean(redirectURL)}
      onSubmit={onSubmit}
      error={error}
      bodyStyles={{ p: ['sp_16', 'sp_20', 'sp_24', 'sp_32'] }}
    >
      <ConfigurationTrap
        title={title}
        alternativeVariations={alternativeVariations}
        setRedirectURL={setRedirectURL}
        selectedValues={selectedValues}
        listLength={listLength}
      />
      {isLoading && <StyledSpinner strokeColor="sys.neutral.border.hover" size={SPINNER_SIZE} />}
    </DrawerForm>
  );
};

export default ConfigurationTrapDrawerBody;
